import { useDeploymentStatuses } from '../useDeploymentStatuses';
import { AppStatusTag } from '../components/AppStatusTag';
import { useEffect, useState } from 'react';
import { APP_TYPES } from '../appTypes';
import { CATALOG_SORTS, sortCatalogApps, type CatalogSort } from '../catalogSort';
import { AppTypeFilter } from '../components/AppTypeFilter';

import { Alert, Icon, Input, Tag, Tooltip } from '../ds';
import { CatalogPageHeader } from '../components/CatalogPageHeader';
import { VibesIcon } from '../components/VibesIcon';
import { icons } from '../icons';
import { relativeTime, type AppSummary } from '../api';
import {
  readCatalogFilters,
  writeCatalogFilters,
  type CatalogFilter,
} from '../catalogFilters';
import { c, cardStyle, pageStyle } from '../tokens';

/** Column template shared by the header row and every body row. */
const GRID = 'minmax(240px, 2.4fr) 1fr minmax(115px, 1fr) 1.1fr 0.9fr 24px';

const FILTERS: { key: CatalogFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'owned', label: 'Owned by me' },
  { key: 'public', label: 'Public' },
  { key: 'private', label: 'Private' },
  { key: 'archived', label: 'Archived' },
];

function PrivateAppIndicator() {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <Tooltip content="Private app" side="top" open={hovered || focused}>
      <span
        tabIndex={0}
        role="img"
        aria-label="Private app"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={event => {
          if (event.key === 'Escape') { setHovered(false); setFocused(false); }
        }}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 24, borderRadius: 6, color: c.textMuted, flex: '0 0 auto', cursor: 'help' }}
      >
        <VibesIcon name="lock" size={16} />
      </span>
    </Tooltip>
  );
}

interface CatalogProps {
  apps: AppSummary[];
  loading: boolean;
  error: string | null;
  pendingCount: number;
  userId: string;
  onOpenApp: (repo: string) => void;
  onCreate: () => void;
  onRetry: () => void;
}

export function Catalog({
  apps,
  loading,
  error,
  pendingCount,
  userId,
  onOpenApp,
  onCreate,
  onRetry,
}: CatalogProps) {
  const [filters, setFilters] = useState<CatalogFilter[]>(() => readCatalogFilters(userId));
  const [sort, setSort] = useState<CatalogSort>('updated');
  const [query, setQuery] = useState('');
  const [appTypes, setAppTypes] = useState<string[]>([]);

  const needle = query.trim().toLowerCase();
  useEffect(() => writeCatalogFilters(userId, filters), [filters, userId]);

  const toggleFilter = (filter: CatalogFilter, checked: boolean): void => {
    setFilters(current => {
      if (filter === 'archived') {
        return checked
          ? [...current, filter]
          : current.filter(item => item !== filter);
      }
      if (filter === 'all') {
        return checked
          ? ['all', ...current.filter(item => item === 'archived')]
          : current.filter(item => item !== filter);
      }
      const withoutAll = current.filter(item => item !== 'all');
      return checked
        ? [...withoutAll, filter]
        : withoutAll.filter(item => item !== filter);
    });
  };

  const matchingApps = apps.filter(app => {
    if (needle && !`${app.title} ${app.repo} ${app.description}`.toLowerCase().includes(needle)) {
      return false;
    }
    if (app.archived) return filters.includes('archived');
    return filters.includes('all')
      || (filters.includes('owned') && app.isOwner)
      || (filters.includes('public') && app.visibility === 'public')
      || (filters.includes('private') && app.visibility === 'private');
  });
  const candidates = sortCatalogApps(matchingApps.filter(app => appTypes.length === 0 || (app.appType !== undefined && appTypes.includes(app.appType))), sort);
  const statuses = useDeploymentStatuses(candidates.filter(app => !app.archived).map(app => app.repo));
  const visible = candidates.filter(app => !statuses.denied.includes(app.repo));
  const typeCount = (type: string) => matchingApps.filter(app => !statuses.denied.includes(app.repo) && app.appType === type).length;

  return (
    <div className="v2-page catalog-page" style={{ ...pageStyle, gap: 24 }}>
      <CatalogPageHeader
        title="Abridge Apps"
        description="Apps built by Abridgers. Select one to learn more or open it."
        action="Create an app"
        onCreate={onCreate}
      />

      {error && (
        <Alert variant="danger" showIcon>
          <span style={{ fontSize: 14, lineHeight: 1.35 }}>
            {error}{' '}
            <button
              type="button"
              onClick={onRetry}
              style={{
                background: 'none',
                border: 0,
                padding: 0,
                font: 'inherit',
                color: c.brandLink,
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
          </span>
        </Alert>
      )}

      {pendingCount > 0 && (
        <Alert variant="info" showIcon>
          <span style={{ fontSize: 14, lineHeight: 1.35 }}>
            {pendingCount === 1
              ? 'One app is waiting for infrastructure approval.'
              : `${pendingCount} apps are waiting for infrastructure approval.`}
          </span>
        </Alert>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ width: 280 }}>
          <Input
            placeholder="Filter apps"
            size="md"
            value={query}
            onChange={event => setQuery(event.target.value)}
            aria-label="Filter apps"
          />
        </div>
        <AppTypeFilter types={APP_TYPES} selected={appTypes} total={matchingApps.filter(app => !statuses.denied.includes(app.repo)).length} count={typeCount} onChange={setAppTypes} />
        <span style={{ marginLeft: 'auto', fontSize: 13, color: c.textMuted }}>
          {loading ? 'Loading…' : `${visible.length} ${visible.length === 1 ? 'app' : 'apps'}`}
        </span>
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {FILTERS.map(item => (
            <label
              key={item.key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                padding: '7px 12px',
                borderRadius: 8,
                background: filters.includes(item.key) ? c.teal : c.subtle,
                color: filters.includes(item.key) ? c.surface : c.text,
                fontSize: 13.5,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <input
                type="checkbox"
                checked={filters.includes(item.key)}
                onChange={event => toggleFilter(item.key, event.target.checked)}
                style={{ margin: 0, accentColor: c.teal }}
              />
              <span>{item.label}</span>
            </label>
          ))}
          <label className="catalog-sort-pill">
            <span>Sort by:</span>
            <select aria-label="Sort apps by" value={sort} onChange={event => setSort(event.target.value as CatalogSort)}>
              {CATALOG_SORTS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
          </label>
      </div>
      <div style={{ ...cardStyle, overflowX: 'auto' }}><div style={{ minWidth: 960 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: GRID,
            gap: 16,
            padding: '12px 20px',
            background: c.subtle,
            borderBottom: `1px solid ${c.border}`,
            fontSize: 12,
            fontWeight: 475,
            letterSpacing: '0.24px',
            color: c.textMuted,
          }}
        >
          <span>App</span>
          <span>Owner</span>
          <span>Category</span>
          <span>Status</span>
          <span>Last update</span>
          <span />
        </div>

        {!loading && visible.length === 0 && (
          <div
            style={{
              padding: '40px 20px',
              textAlign: 'center',
              color: c.textFaint,
              fontSize: 13.5,
            }}
          >
            {apps.length === 0
              ? 'No apps yet — create your first one from a platform template.'
              : 'No apps match this filter.'}
          </div>
        )}

        {visible.map(app => {
          return (
            <div
              key={app.repo}
              role="button"
              tabIndex={0}
              onClick={() => onOpenApp(app.repo)}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onOpenApp(app.repo);
                }
              }}
              style={{
                display: 'grid',
                gridTemplateColumns: GRID,
                gap: 16,
                padding: '16px 20px',
                borderBottom: `1px solid ${c.border}`,
                borderTop: 0,
                borderLeft: 0,
                borderRight: 0,
                alignItems: 'center',
                width: '100%',
                background: app.archived ? c.subtle : 'transparent',
                font: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
              }}
              onMouseEnter={event => {
                event.currentTarget.style.background = c.canvas;
              }}
              onMouseLeave={event => {
                event.currentTarget.style.background = app.archived ? c.subtle : 'transparent';
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    minWidth: 0,
                    flexWrap: 'wrap',
                  }}
                >
                  <span
                    style={{
                      fontSize: 15,
                      fontWeight: 475,
                      letterSpacing: '0.28px',
                      color: c.text,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {app.title}
                  </span>
                  {app.visibility === 'private' && (
                    <PrivateAppIndicator />
                  )}
                  {app.archived && (
                    <Tag variant="default" size="sm">Archived</Tag>
                  )}
                </div>
                <span style={{ fontSize: 13, lineHeight: 1.35, color: c.textBody }}>
                  {app.description}
                </span>
              </div>

              {app.primaryOwner ? (
                <a
                  href={`https://github.com/${encodeURIComponent(app.primaryOwner)}`}
                  target="_blank"
                  rel="noreferrer"
                  onClick={event => event.stopPropagation()}
                  onKeyDown={event => event.stopPropagation()}
                  style={{ fontSize: 13, color: c.textBody, textDecoration: 'underline' }}
                >
                  {app.primaryOwner}
                </a>
              ) : (
                <span style={{ fontSize: 13, color: c.textBody }}>unknown</span>
              )}

              <div>{app.appType ? <span className="v2-type-tag">{app.appType}</span> : <span style={{ fontSize: 13, color: c.textFaint }}>Not set</span>}</div>

              <div>
                <AppStatusTag app={app} size="sm" history={statuses.histories[app.repo] ?? null} unavailable={statuses.unavailable.includes(app.repo)} />
              </div>

              <span style={{ fontSize: 13, color: c.textMuted }}>{relativeTime(app.pushedAt)}</span>

              <Icon name={icons.chevronRight} size="sm" color={c.textFaint} />
            </div>
          );
        })}
      </div></div>
    </div>
  );
}
